import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { PrototypeDto } from '@aiai/shared';
import { setPrototypeProduction } from '../api';
import { invalidatePrototype } from '../api/prototypeCache';
import { errorMessage } from '../lib/api';
import { Callout } from './Callout';
import { SectionCard } from './SectionCard';
import { Spinner } from './Spinner';

/**
 * The "In production" switch. Idle prototypes (no visit for a day) are put to
 * sleep and wake on the next visit; an in-production one never sleeps.
 * Owners and project contributors may change it (`prototype:operate`);
 * everyone else sees it read-only.
 */
export function PrototypeProductionCard({
  prototype,
  canOperate,
}: {
  prototype: PrototypeDto;
  canOperate: boolean;
}) {
  const queryClient = useQueryClient();
  const on = prototype.deployment.inProduction;

  const toggleMutation = useMutation({
    mutationFn: (next: boolean) => setPrototypeProduction(prototype.id, next),
    onSuccess: () => invalidatePrototype(queryClient, prototype.id),
  });

  return (
    <SectionCard title="Availability">
      <div className="space-y-3">
        <div className="flex items-center gap-3">
          <button
            id="prototype-in-production"
            type="button"
            role="switch"
            aria-checked={on}
            aria-labelledby="prototype-in-production-label"
            disabled={!canOperate || toggleMutation.isPending}
            onClick={() => toggleMutation.mutate(!on)}
            className={`relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
              on ? 'bg-ink' : 'bg-line'
            }`}
          >
            <span
              aria-hidden="true"
              className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                on ? 'translate-x-[18px]' : 'translate-x-0.5'
              }`}
            />
          </button>
          <span id="prototype-in-production-label" className="text-sm font-medium text-ink">
            In production
          </span>
          {toggleMutation.isPending && <Spinner className="h-3.5 w-3.5" />}
        </div>
        <p className="text-xs text-ink-faint">Never put to sleep when idle. Turn on for apps people rely on.</p>
        {toggleMutation.isError && (
          <Callout tone="danger">{errorMessage(toggleMutation.error, 'Could not change this setting')}</Callout>
        )}
      </div>
    </SectionCard>
  );
}
